import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Container,
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    Divider,
    useTheme,
} from '@mui/material';
import {
    Speed as SpeedIcon,
    Psychology as PsychologyIcon,
    ArrowForward as ArrowForwardIcon,
    Bolt as BoltIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useOfferDecision } from '../utils/offerDecision';
import FlexCardVisual from '../components/flex/FlexCardVisual';
import { generateQrCodeDataUrl } from '../utils/qrCode';
import { trackFlexEvent, FLEX_EVENTS } from '../utils/flexAnalytics';

export default function FlexCardLanding() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { user, isAuthenticated } = useAuth();
    const { offer, hasAccess, lifecycleState } = useOfferDecision({ source: 'flex_card' });
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);

    useEffect(() => {
        async function fetchCard() {
            try {
                const res = await api.get(`/flex/cards/${cardId}`);
                setCard(res.data);
                trackFlexEvent(FLEX_EVENTS.CARD_VIEWED, {
                    card_id: cardId,
                    card_type: res.data.cardType,
                });
            } catch (err) {
                setError(err.response?.data?.error || 'Card not found.');
            } finally {
                setLoading(false);
            }
        }
        fetchCard();
    }, [cardId]);

    useEffect(() => {
        if (card?.shareUrl) {
            generateQrCodeDataUrl(card.shareUrl, { width: 200 }).then(setQrCodeDataUrl);
        }
    }, [card?.shareUrl]);

    const handleCta = () => {
        navigate(offer.route);
    };

    const ctaLabel = offer.label;
    const ctaSubtext = offer.subtext;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" color="error" gutterBottom>
                    {error}
                </Typography>
                <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                    Go to ReaderFLEX
                </Button>
            </Container>
        );
    }

    const ogTitle = card.headline ? `${card.headline} | ReaderFLEX` : 'ReaderFLEX Achievement';
    const ogDescription = card.subheadline
        ? `${card.subheadline} achieved ${card.headline} on ReaderFLEX`
        : 'See this reading achievement on ReaderFLEX';
    const ogImage = `${process.env.REACT_APP_API_URL || ''}/api/flex/cards/${cardId}/image/og`;

    return (
        <>
            <Helmet>
                <title>{ogTitle}</title>
                <meta name="description" content={ogDescription} />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={ogImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={ogTitle} />
                <meta name="twitter:description" content={ogDescription} />
                <meta name="twitter:image" content={ogImage} />
            </Helmet>

            <Container maxWidth="sm" sx={{ py: 4 }}>
                {/* Card display */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <FlexCardVisual card={card} qrCodeDataUrl={qrCodeDataUrl} showQr={true} />
                </Box>

                {/* Headline and description */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {card.headline}
                    </Typography>
                    {card.subheadline && (
                        <Typography variant="body1" color="text.secondary">
                            {card.subheadline}
                        </Typography>
                    )}
                </Box>

                {/* Metrics summary (if available) */}
                {card.metrics && Object.keys(card.metrics).length > 0 && (
                    <Paper sx={{ p: 3, mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                            {card.metrics.wpm != null && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <SpeedIcon color="primary" sx={{ fontSize: 28, mb: 0.5 }} />
                                    <Typography variant="h6" fontWeight={700}>{card.metrics.wpm}</Typography>
                                    <Typography variant="caption" color="text.secondary">WPM</Typography>
                                </Box>
                            )}
                            {card.metrics.comprehension != null && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <PsychologyIcon color="primary" sx={{ fontSize: 28, mb: 0.5 }} />
                                    <Typography variant="h6" fontWeight={700}>{card.metrics.comprehension}%</Typography>
                                    <Typography variant="caption" color="text.secondary">Comprehension</Typography>
                                </Box>
                            )}
                            {card.metrics.effectiveWpm != null && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <BoltIcon sx={{ fontSize: 28, mb: 0.5, color: '#FF9800' }} />
                                    <Typography variant="h6" fontWeight={700}>{card.metrics.effectiveWpm}</Typography>
                                    <Typography variant="caption" color="text.secondary">Effective WPM</Typography>
                                </Box>
                            )}
                            {card.metrics.streak != null && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" fontWeight={700}>{card.metrics.streak}</Typography>
                                    <Typography variant="caption" color="text.secondary">Day Streak</Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                )}

                <Divider sx={{ mb: 4 }} />

                {/* Lifecycle-aware CTA */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {hasAccess
                            ? 'Ready for your next session?'
                            : lifecycleState === 'guest'
                            ? 'Want to read like this?'
                            : 'Ready to keep training?'
                        }
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                        {ctaSubtext}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleCta}
                        endIcon={<ArrowForwardIcon />}
                        sx={{ py: 1.5, px: 5 }}
                    >
                        {ctaLabel}
                    </Button>
                </Box>

                {/* Referral attribution note */}
                {card.referralCode && !isAuthenticated && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 3 }}
                    >
                        You were referred by @{card.referralCode}
                    </Typography>
                )}
            </Container>
        </>
    );
}
